import { SetStateAction } from "react";
import {
  createContext,
  Dispatch,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";

import { useUnitAndBuilding } from "../../services/api/hooks/05_useUnitAndBuilding";
import { useSearchBuildings } from "../../services/api/hooks/02_useSearchBuildings";
import { useBlock } from "../../services/api/hooks/03_useBlock";
import {
  IBuilding,
  IBuildingBlock,
  IBuildingBlockUnit,
  IBuildingSearch,
  IBuildingBlockSearch,
  SEARCH_TEXT_ADDRESS_KEY,
  BLOCK_SEARCH_KEY,
} from "../../graphql/types";
import { SIZE_UNIT } from "../../enum";
import { routes } from "../../routes";

import { BuildingBlock, BuildingBlockUnit } from "../types";

const AddressSearchContext = createContext(null);

export const useAddressSearch = () => {
  const addressSearch = useContext(AddressSearchContext);
  if (!addressSearch) {
    throw new Error(
      `You must call useAddressSearch inside of a <AddressSearchProvider />`
    );
  }
  return addressSearch;
};

interface AddressSearchProviderProps {
  children: React.ReactNode;
  resultUrl: string;
}

interface SearchPropertiesProps {
  unit: (typeof SIZE_UNIT)[keyof typeof SIZE_UNIT];
  min: number;
  max: number;
}

export interface WrappedProps {
  selectedBuilding: IBuilding;
  selectedBuildingBlock: IBuildingBlock;
  selectedBuildingBlockUnit: IBuildingBlockUnit;
  setSelectedBuilding: Dispatch<IBuilding>;
  setSelectedBuildingBlock: Dispatch<IBuildingBlock>;
  setSelectedBuildingBlockUnit: Dispatch<IBuildingBlockUnit>;
  mapZoomLevel: number;
  setMapZoomLevel: Dispatch<number>;
  searchProperties: SearchPropertiesProps;
  setSearchProperties: Dispatch<SearchPropertiesProps>;
  searchBuildings: (options: { variables: { input: string } }) => Promise<void>;
  resultBuildings: IBuildingSearch | null;
  loadingBuildings: boolean;
  searchBuildingBlocks: (options: {
    variables: { block_id: string };
  }) => Promise<void>;
  resultBuildingBlocks: IBuildingBlockSearch | null;
  loadingBuildingBlocks: boolean;
  searchBuildingBlockUnits: (options: {
    variables: { unit_id: string; building_id: string };
  }) => Promise<void>;
  resultBuildingBlockUnits: IBuildingBlockUnit | null;
  loadingBuildingBlockUnits: boolean;
  buildings: IBuildingSearch[typeof SEARCH_TEXT_ADDRESS_KEY];
  buildingBlocks: BuildingBlock[];
  setBuildingBlocks: Dispatch<SetStateAction<BuildingBlock[]>>;
  buildingBlockUnits: BuildingBlockUnit[];
  setBuildingBlockUnits: Dispatch<SetStateAction<BuildingBlockUnit[]>>;
}

export const AddressSearchProvider = ({
  children,
  resultUrl = routes.root,
}: AddressSearchProviderProps) => {
  // REACT CONTEXTS
  const [buildings, setBuildings] = useState<
    IBuildingSearch[typeof SEARCH_TEXT_ADDRESS_KEY]
  >([]);
  const [buildingBlocks, setBuildingBlocks] = useState<BuildingBlock[]>([]);
  const [buildingBlockUnits, setBuildingBlockUnits] = useState<
    BuildingBlockUnit[]
  >([]);

  // Use the new Supabase hook for block search
  const {
    data: blockData,
    loading: loadingBuildingBlocks,
    getBlock,
  } = useBlock();

  // Create a GraphQL-compatible wrapper function for block search
  const searchBuildingBlocks = useCallback(
    (options: { variables: { block_id: string } }) => {
      return getBlock(options.variables.block_id);
    },
    [getBlock]
  );

  // Map the block data to match GraphQL response format
  const resultBuildingBlocks = useMemo(() => {
    if (!blockData?.block) {
      return null;
    }
    return {
      [BLOCK_SEARCH_KEY]: { ...blockData.block },
    };
  }, [blockData]);

  // Use the new Supabase hook for building search
  const {
    data: buildingSearchData,
    loading: loadingBuildings,
    searchBuildings: searchBuildingsFunction,
  } = useSearchBuildings();

  // Create a GraphQL-compatible wrapper function for building search
  const searchBuildings = useCallback(
    (options: { variables: { input: string } }) => {
      return searchBuildingsFunction(options.variables.input);
    },
    [searchBuildingsFunction]
  );

  // Map the building search data to match GraphQL response format
  const resultBuildings = useMemo(() => {
    // Handle both nested and direct array formats
    const searchResults =
      buildingSearchData?.SearchTextAddress || buildingSearchData;

    if (!searchResults || !Array.isArray(searchResults)) {
      return null;
    }

    return {
      [SEARCH_TEXT_ADDRESS_KEY]: searchResults.map(building => ({
        ...building,
        score: String(building.score), // Convert number to string
      })),
    };
  }, [buildingSearchData]);

  // Use the new Supabase hook for unit and building search
  const {
    data: unitAndBuildingData,
    loading: loadingBuildingBlockUnits,
    getUnitAndBuilding,
  } = useUnitAndBuilding();

  // Create a GraphQL-compatible wrapper function
  const searchBuildingBlockUnits = useCallback(
    (options: { variables: { unit_id: string; building_id: string } }) => {
      return getUnitAndBuilding(
        options.variables.unit_id,
        options.variables.building_id
      );
    },
    [getUnitAndBuilding]
  );

  // Map the new data structure to match GraphQL response format with proper memoization
  const resultBuildingBlockUnits = useMemo(() => {
    if (!unitAndBuildingData?.unit || !unitAndBuildingData?.building) {
      return null;
    }

    return unitAndBuildingData;
  }, [unitAndBuildingData]);

  // SELECTED
  const [selectedBuilding, setSelectedBuilding] = useState<IBuilding>(null);
  const [selectedBuildingBlock, setSelectedBuildingBlock] =
    useState<IBuildingBlock>(null);
  const [selectedBuildingBlockUnit, setSelectedBuildingBlockUnit] =
    useState<IBuildingBlockUnit>(null);
  const [mapZoomLevel, setMapZoomLevel] = useState(3);
  const [searchProperties, setSearchProperties] =
    useState<SearchPropertiesProps>({
      unit: SIZE_UNIT.IN_METERS,
      min: 0,
      max: 100,
    });

  useEffect(() => {
    if (resultBuildings) {
      setBuildings(resultBuildings[SEARCH_TEXT_ADDRESS_KEY]);
    }
  }, [resultBuildings]);

  useEffect(() => {
    if (selectedBuilding && resultBuildingBlocks) {
      const newBuildingBlock = resultBuildingBlocks[BLOCK_SEARCH_KEY];
      setSelectedBuildingBlock(newBuildingBlock);
      setSelectedBuildingBlockUnit(null);
      setBuildingBlockUnits(
        newBuildingBlock.unit_ids.reduce((acc, val, idx) => {
          return acc.concat({
            _id: val,
            name: newBuildingBlock.unit_names[idx],
          });
        }, [])
      );
    }
  }, [
    selectedBuilding,
    setSelectedBuildingBlock,
    setSelectedBuildingBlockUnit,
    resultBuildingBlocks,
    setBuildingBlockUnits,
  ]);

  useEffect(() => {
    if (resultBuildingBlockUnits) {
      setSelectedBuildingBlockUnit(resultBuildingBlockUnits);
    }
  }, [resultBuildingBlockUnits]);

  const wrapped: WrappedProps = {
    selectedBuilding,
    selectedBuildingBlock,
    selectedBuildingBlockUnit,
    setSelectedBuilding,
    setSelectedBuildingBlock,
    setSelectedBuildingBlockUnit,
    mapZoomLevel,
    setMapZoomLevel,
    searchProperties,
    setSearchProperties,
    searchBuildings,
    resultBuildings,
    loadingBuildings,
    searchBuildingBlocks,
    resultBuildingBlocks,
    loadingBuildingBlocks,
    searchBuildingBlockUnits,
    resultBuildingBlockUnits,
    loadingBuildingBlockUnits,
    buildings,
    buildingBlocks,
    setBuildingBlocks,
    buildingBlockUnits,
    setBuildingBlockUnits,
  };

  return (
    <AddressSearchContext.Provider value={wrapped}>
      {children}
    </AddressSearchContext.Provider>
  );
};
